import React, { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { formatTableLabel } from "./dineInBoardBuilder";
import { buildTableQrUrl, buildNineQrImageSettings } from "./dineInQrHelper";
import "./styles/DineInQrCard.css";

interface DineInQrCardProps {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  tableNumber: number;
  /** Rendered pixel size of the QR itself. */
  size?: number;
  onOpen?: (tableNumber: number) => void;
}

/**
 * One table's QR, laid out as the card a customer actually reads: whose
 * restaurant it is, which table they're at, the code, and what it does.
 *
 * Two brands share this card by necessity, not preference. The QR's centre
 * holds Nine's logo — a code has room for exactly one image — so the
 * restaurant's own mark and name sit above it. Nine is inside the code, the
 * restaurant is around it.
 *
 * level="H" (30% error correction) is required, not stylistic: the centre logo
 * punches a hole that error correction has to reconstruct. See
 * buildNineQrImageSettings.
 */
const DineInQrCard: React.FC<DineInQrCardProps> = ({
  businessId,
  businessName,
  businessLogoUrl,
  tableNumber,
  size = 116,
  onOpen,
}) => {
  // A logo that 404s renders as a browser broken-image glyph, and this card is
  // the thing a customer looks at while deciding whether to trust the code.
  // Drop it silently instead — the business name below carries the identity.
  const [logoFailed, setLogoFailed] = useState(false);

  const value = buildTableQrUrl(businessId, tableNumber);
  const content = (
    <>
      {businessLogoUrl && !logoFailed && (
        <img
          className="dine-in-qr-card__logo"
          src={businessLogoUrl}
          alt=""
          aria-hidden="true"
          onError={() => setLogoFailed(true)}
        />
      )}
      <span className="dine-in-qr-card__business">{businessName}</span>
      <span className="dine-in-qr-card__table">{formatTableLabel(tableNumber)}</span>
      <span className="dine-in-qr-card__code">
        <QRCodeCanvas
          value={value}
          size={size}
          level="H"
          includeMargin={false}
          imageSettings={buildNineQrImageSettings(size)}
        />
      </span>
      <span className="dine-in-qr-card__hint">Scan to order from your phone</span>
    </>
  );

  if (!onOpen) {
    return <div className="dine-in-qr-card">{content}</div>;
  }

  return (
    <button
      type="button"
      className="dine-in-qr-card dine-in-qr-card--tappable"
      onClick={() => onOpen(tableNumber)}
      aria-label={`Enlarge QR code for table ${tableNumber}`}
    >
      {content}
    </button>
  );
};

export default DineInQrCard;
